import { ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHasPermission, useHasAnyPermission, useHasAllPermissions, Permission } from 'contexts/PermissionContext';
import { Box, Typography, Button } from '@mui/material';
import { ArrowLeft } from 'iconsax-react';
import useSelection from 'hooks/useSelection';

interface PermissionGuardProps {
  children: ReactElement;
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean;
  redirectTo?: string; // Where to redirect if no permission
  showAccessDenied?: boolean; // Show access denied page instead of redirecting
  renderDisabled?: boolean; // Render content in disabled/read-only state instead of showing access denied
}

/**
 * Permission Guard Component
 * Protects routes based on user permissions
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  redirectTo = '/',
  showAccessDenied = false,
  renderDisabled = false
}) => {
  const navigate = useNavigate();
  
  // Always call hooks at the top level
  const hasPermission = useHasPermission(permission);
  const hasAnyPermission = useHasAnyPermission(permissions || []);
  const hasAllPermissions = useHasAllPermissions(permissions || []);
  const { isLoadingClients } = useSelection();

  // Calculate access
  let hasAccess = true;
  if (permission) {
    hasAccess = hasPermission;
  } else if (permissions && permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions : hasAnyPermission;
  }

//   useEffect(() => {
//     if (!hasAccess && !showAccessDenied) {
//       navigate(redirectTo, { replace: true });
//     }
//   }, [hasAccess, navigate, redirectTo, showAccessDenied]);

  // If this is the dashboard route and user doesn't have access, redirect to transactions
  // But wait for clients to finish loading first to ensure proper initialization
  useEffect(() => {
    if (!hasAccess && !showAccessDenied && !isLoadingClients && window.location.pathname === '/') {
      console.log('PermissionGuard: Redirecting to transactions after client loading completed');
      navigate('/transactions', { replace: true });
    } else if (!hasAccess && !showAccessDenied && isLoadingClients && window.location.pathname === '/') {
      console.log('PermissionGuard: Waiting for client loading to complete before redirecting...');
    }
  }, [hasAccess, navigate, showAccessDenied, isLoadingClients]);

  if (!hasAccess) {
    if (showAccessDenied) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            p: 3
          }}
        >
          <Typography variant="h4" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600 }}>
            You don't have the required permissions to access this page. 
            {permission && ` Required permission: "${permission}"`}
            {permissions && permissions.length > 0 && ` Required permissions: ${permissions.join(', ')}`}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowLeft />}
            onClick={() => navigate(-1)} // Go back to previous page
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      );
    } else if (renderDisabled) {
      // Render content in disabled/read-only state
      return (
        <Box
          sx={{
            position: 'relative',
            opacity: 0.6,
            // Remove pointerEvents: 'none' so overlay can block interactions
            '& *': {
              userSelect: 'none',
              cursor: 'not-allowed !important'
            },
            '& input, & textarea, & select': {
              // backgroundColor: 'action.disabledBackground',
              // color: 'text.disabled'
            },
            '& button': {
              opacity: 0.5,
              pointerEvents: 'none'
            }
          }}
        >
          {/* Overlay to prevent any interaction */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              backgroundColor: 'rgba(255, 255, 255, 0)', // transparent but blocks events
              pointerEvents: 'all', // block all mouse events
            }}
          />
          {children}
        </Box>
      );
    }
    return null;
  }

  return children;
};

export default PermissionGuard;
